import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { caseDetailsI18n } from './i18n';

const EditCaseModal = ({
  DesignSystemComponents,
  show,
  caseData,
  courts = [],
  group = [],
  services = [],
  businessLines = [],
  internalStates = [],
  onBusinessLineChange,
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation(undefined, { i18n: caseDetailsI18n });
  const { Input, Modal, ComboBox } = DesignSystemComponents;

  const [form, setForm] = useState({
    id: 0,
    active: true,
    title: '',
    code: '',
    courtId: '',
    businessAreaId: '',
    serviceId: '',
    lineaNegocioId: '',
    internalStateId: null,
  });

  const [selectedCourts, setSelectedCourts] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedBusinessLine, setSelectedBusinessLine] = useState(null);
  const [selectedActiveState, setSelectedActiveState] = useState(null);
  const [selectedInactiveState, setSelectedInactiveState] = useState(null);
  const [activeStateOptions, setActiveStateOptions] = useState([]);
  const [inactiveStateOptions, setInactiveStateOptions] = useState([]);

  const businessLineOptions = businessLines.map((line) => ({
    label: line.name || t('caseDetails_noInfo'),
    value: line.id,
  }));

  useEffect(() => {
    if (!show) return;
    if (!caseData) return;

    setForm({
      id: caseData.id,
      active: caseData.active,
      title: caseData.title || '',
      code: caseData.code || '',
      courtId: caseData.courtId || '',
      businessAreaId: caseData.businessAreaId || '',
      serviceId: caseData.serviceId || null,
      lineaNegocioId: caseData.lineaNegocioId ?? caseData.businessLineId ?? '',
      internalStateId: caseData.internalStateId ?? null,
    });

    setSelectedCourts(
      caseData.courtId
        ? { label: caseData.court, value: caseData.courtId }
        : null
    );

    setSelectedGroup(
      caseData.businessAreaId
        ? { label: caseData.group, value: caseData.businessAreaId }
        : null
    );

    const caseService = services.find((s) => s.id === caseData.serviceId);
    setSelectedService(
      caseService ? { label: caseService.name, value: caseService.id } : null
    );
    const businessLineId =
      caseData.lineaNegocioId ?? caseData.businessLineId ?? null;
    const businessLineOption =
      businessLineId != null
        ? businessLineOptions.find(
            (line) => Number(line.value) === Number(businessLineId)
          )
        : null;
    setSelectedBusinessLine(businessLineOption || null);
    setSelectedActiveState(null);
    setSelectedInactiveState(null);
  }, [show, caseData?.id, businessLines]);

  useEffect(() => {
    const items = Array.isArray(internalStates) ? internalStates : [];

    const resolveStatus = (item) => {
      const raw =
        item?.status ?? item?.active ?? item?.isActive ?? item?.activo ?? null;
      if (raw === true) return 1;
      if (raw === false) return 0;
      const asNumber = Number(raw);
      return Number.isFinite(asNumber) ? asNumber : null;
    };

    const mapped = items.map((item) => ({
      label: item.description ?? item.name ?? t('caseDetails_noInfo'),
      value: item.id,
      status: resolveStatus(item),
    }));

    setActiveStateOptions(mapped.filter((item) => item.status === 1));
    setInactiveStateOptions(mapped.filter((item) => item.status === 0));
  }, [internalStates]);

  useEffect(() => {
    if (!show) return;
    if (!form.internalStateId) return;

    const selectedActive = activeStateOptions.find(
      (opt) => Number(opt.value) === Number(form.internalStateId)
    );
    const selectedInactive = inactiveStateOptions.find(
      (opt) => Number(opt.value) === Number(form.internalStateId)
    );

    setSelectedActiveState(selectedActive ?? null);
    setSelectedInactiveState(selectedInactive ?? null);
  }, [show, form.internalStateId, activeStateOptions, inactiveStateOptions]);

  useEffect(() => {
    if (!show) return;
    if (form.active === true) {
      const nextId = selectedActiveState ? selectedActiveState.value : null;
      if (form.internalStateId !== nextId) {
        setForm((prev) => ({ ...prev, internalStateId: nextId }));
      }
      return;
    }
    if (form.active === false) {
      const nextId = selectedInactiveState ? selectedInactiveState.value : null;
      if (form.internalStateId !== nextId) {
        setForm((prev) => ({ ...prev, internalStateId: nextId }));
      }
    }
  }, [show, form.active, selectedActiveState, selectedInactiveState]);

  useEffect(() => {
    if (!show) return;
    if (!form.serviceId) {
      setSelectedService(null);
      return;
    }
    const caseService = services.find((s) => s.id === form.serviceId);
    if (caseService) {
      setSelectedService({ label: caseService.name, value: caseService.id });
    }
  }, [show, services, form.serviceId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (selected, setSelected, field) => {
    setSelected(selected);
    setForm((prev) => ({
      ...prev,
      [field]: selected ? selected.value : null,
    }));
  };

  const handleBusinessLineSelect = (selected) => {
    handleDropdownChange(selected, setSelectedBusinessLine, 'lineaNegocioId');
    setSelectedService(null);
    setForm((prev) => ({ ...prev, serviceId: null }));
    if (onBusinessLineChange) {
      onBusinessLineChange(selected ? selected.value : null);
    }
  };

  const handleSave = () => {
    onEdit(form);
    onClose();
  };

  const hasReasonSelected =
    form.active === true
      ? Boolean(selectedActiveState)
      : Boolean(selectedInactiveState);

  return (
    <Modal
      title={t('caseDetails_editCaseModal')}
      show={show}
      onClose={onClose}
      primaryBtnTxt={t('caseDetails_saveCaseModalButton')}
      primaryBtnAction={handleSave}
      primaryBtnDisabled={!hasReasonSelected}
      secondaryBtnTxt={t('caseDetails_closeFilterButton')}
      secondaryBtnAction={onClose}
      ctaAligment='center'
    >
      <p className='font-light mb-2'>{t('caseDetails_caseInfoModal')}</p>
      <div className='d-flex gap-3 mb-3'>
        <label className='d-flex align-items-center gap-2'>
          <input
            type='radio'
            name='active'
            checked={form.active === true}
            onChange={() =>
              handleChange({ target: { name: 'active', value: true } })
            }
          />
          {t('caseDetails_activeStatus')}
        </label>
        <label className='d-flex align-items-center gap-2'>
          <input
            type='radio'
            name='active'
            checked={form.active === false}
            onChange={() =>
              handleChange({ target: { name: 'active', value: false } })
            }
          />
          {t('caseDetails_inactiveStatus')}
        </label>
      </div>
      {form.active === true && (
        <ComboBox
          label={t('caseDetails_activeReason')}
          options={activeStateOptions}
          value={selectedActiveState}
          onChange={(selected) => {
            setSelectedActiveState(selected);
            setForm((prev) => ({
              ...prev,
              internalStateId: selected ? selected.value : null,
            }));
          }}
          placeholder={t('caseDetails_selectReasonPlaceholder')}
        />
      )}
      {form.active === false && (
        <ComboBox
          label={t('caseDetails_inactiveReason')}
          options={inactiveStateOptions}
          value={selectedInactiveState}
          onChange={(selected) => {
            setSelectedInactiveState(selected);
            setForm((prev) => ({
              ...prev,
              internalStateId: selected ? selected.value : null,
            }));
          }}
          placeholder={t('caseDetails_selectReasonPlaceholder')}
        />
      )}
      <Input
        label={t('caseDetails_caseFilter')}
        name='title'
        placeholder={t('caseDetails_casePlaceholder')}
        type='text'
        value={form.title}
        onChange={handleChange}
      />
      <Input
        label={t('caseDetails_roleFilter')}
        name='code'
        placeholder={t('caseDetails_rolePlaceholder')}
        type='text'
        value={form.code}
        onChange={handleChange}
      />
      <ComboBox
        label={t('caseDetails_court')}
        options={courts}
        value={selectedCourts}
        onChange={(selected) =>
          handleDropdownChange(selected, setSelectedCourts, 'courtId')
        }
        placeholder={t('caseDetails_selectCourtPlaceholder')}
      />
      <ComboBox
        label={t('caseDetails_groupFilter')}
        options={group.map((g) => ({
          label: g.name || 'Sin nombre',
          value: g.value || g.id,
        }))}
        value={selectedGroup}
        onChange={(selected) =>
          handleDropdownChange(selected, setSelectedGroup, 'businessAreaId')
        }
        placeholder={t('caseDetails_groupPlaceholder')}
      />
      <ComboBox
        label={t('caseDetails_businessLineFilter')}
        options={businessLineOptions}
        value={selectedBusinessLine}
        onChange={handleBusinessLineSelect}
        placeholder={t('caseDetails_businessLinePlaceholder')}
      />
      <ComboBox
        label={t('caseDetails_serviceFilter')}
        options={services.map((g) => ({
          label: g.name,
          value: g.id,
        }))}
        value={selectedService}
        onChange={(selected) =>
          handleDropdownChange(selected, setSelectedService, 'serviceId')
        }
        placeholder={t('caseDetails_servicePlaceholder')}
      />
    </Modal>
  );
};

export default EditCaseModal;
